import {
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementUnit,
} from 'types'

/** All CTP consumers (markup + card) work in mm; keep the unit shared. */
export const CTP_STD_UNIT = GeckoMeasurementUnit.Millimeters

/** The bin resolution + geometry offsets needed to convert a CTP's raw bins. */
export interface CtpResolution {
  binWidth: number
  binHeight: number
  geomStartX: number
  geomStartY: number
}

type CtpAxis = 'x' | 'y'

/** The backend-stamped fields of a `FFS_CRITICAL_THICKNESS_PROFILE` entry. */
interface RawCtpData {
  critical_path_bins?: [number, number][]
  thickness?: (GeckoMeasurementJson | null)[]
  index_axis?: CtpAxis
  wrap_axis?: CtpAxis
  period_bins?: number
  title?: string
  bin_width?: GeckoMeasurementJson
  bin_height?: GeckoMeasurementJson
  x_start?: GeckoMeasurementJson
  y_start?: GeckoMeasurementJson
}

/** A CTP entry's data, read once into typed fields plus derived axis/wrap info. */
export interface CtpEntry {
  bins: [number, number][]
  /** Per-bin thickness, same length/order as `bins`; null = no data. */
  thickness?: (GeckoMeasurementJson | null)[]
  indexAxis?: CtpAxis
  wrapAxis?: CtpAxis
  periodBins?: number
  title?: string
  binWidth?: GeckoMeasurementJson
  binHeight?: GeckoMeasurementJson
  xStart?: GeckoMeasurementJson
  yStart?: GeckoMeasurementJson
  /** Array index (0 = x, 1 = y) of the index axis; falls back to the wrap axis. */
  indexIdx: 0 | 1
  wrapIdx: 0 | 1
  /** `periodBins` as a number; NaN-ish when absent -- gate on `hasWrap`. */
  period: number
  hasWrap: boolean
}

/** The single typed read of a CTP entry's `data`. */
export const parseCtpEntry = (entry: GeckoEntry): CtpEntry => {
  const data: RawCtpData = entry.data
  const wrapAxis = data.wrap_axis
  const indexAxis = data.index_axis
  const period = data.period_bins as number
  return {
    bins: data.critical_path_bins ?? [],
    thickness: Array.isArray(data.thickness) ? data.thickness : undefined,
    indexAxis,
    wrapAxis,
    periodBins: data.period_bins,
    title: data.title,
    binWidth: data.bin_width,
    binHeight: data.bin_height,
    xStart: data.x_start,
    yStart: data.y_start,
    indexIdx: (indexAxis ?? wrapAxis ?? 'x') === 'y' ? 1 : 0,
    wrapIdx: wrapAxis === 'y' ? 1 : 0,
    period,
    hasWrap:
      (wrapAxis === 'x' || wrapAxis === 'y') &&
      Number.isFinite(period) &&
      period > 0,
  }
}

/**
 * Resolves the true bin size + geometry origin for a CTP entry.
 *
 * `critical_path_bins` is indexed on whatever grid the backend computed the
 * profile against -- NOT necessarily the coarsest display CDF. Preferred
 * source is the entry's own explicit resolution fields (backend contract);
 * only entries that predate that contract fall back to a caller-resolved
 * CDF's bin_size/geometry (which may be the wrong grid for legacy data).
 */
export const resolveCtpResolution = (
  ctp: CtpEntry,
  cdf: GeckoComponentDataFrame | undefined
): CtpResolution | undefined => {
  const { binWidth, binHeight, xStart, yStart } = ctp
  if (binWidth && binHeight && xStart && yStart) {
    return {
      binWidth:
        GeckoMeasurement.CreateFromJSON(binWidth).convertTo(CTP_STD_UNIT).num,
      binHeight:
        GeckoMeasurement.CreateFromJSON(binHeight).convertTo(CTP_STD_UNIT).num,
      geomStartX:
        GeckoMeasurement.CreateFromJSON(xStart).convertTo(CTP_STD_UNIT).num,
      geomStartY:
        GeckoMeasurement.CreateFromJSON(yStart).convertTo(CTP_STD_UNIT).num,
    }
  }
  if (cdf) {
    return {
      binWidth: cdf.data.binning.bin_size.width.convertTo(CTP_STD_UNIT).num,
      binHeight: cdf.data.binning.bin_size.height.convertTo(CTP_STD_UNIT).num,
      geomStartX: cdf.data.geometry.start.x.convertTo(CTP_STD_UNIT).num,
      geomStartY: cdf.data.geometry.start.y.convertTo(CTP_STD_UNIT).num,
    }
  }
  return undefined
}

/** Normalizes a raster-bin coordinate into `[0, period)`. */
export const normalizeBin = (v: number, period: number): number =>
  ((v % period) + period) % period
